/********************************************************************
 * ADOBE CONFIDENTIAL
 * __________________
 *
 *  Copyright 2024 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 *******************************************************************/

import { FunctionComponent } from 'preact';
import {
  ChangePasswordProps,
  ValidateLengthConfigProps,
} from '@/account/types';
import { useText } from '@adobe-commerce/elsie/i18n';
import {
  Button,
  Card,
  Header,
  InLineAlert,
  InputPassword,
} from '@adobe-commerce/elsie/components';
import '@/account/components/ChangePassword/ChangePassword.css';
import { useGetStoreConfigs } from '@/account/hooks/containers/useGetStoreConfigs';
import { useChangePassword } from '@/account/hooks/components/useChangePassword';
import { usePasswordValidationMessage } from '@/account/hooks/components/usePasswordValidationMessage';

export const getErrorMessage = (
  isValidUniqueSymbols: string,
  defaultLengthMessage: ValidateLengthConfigProps | undefined,
  isSubmit: boolean,
  newPassword: string,
  passwordErrors: { newPassword: string }
) => {
  const hasError =
    isValidUniqueSymbols === 'error' ||
    defaultLengthMessage?.status === 'error' ||
    (isSubmit && newPassword.trim().length === 0);

  return hasError ? passwordErrors.newPassword : undefined;
};

export const ChangePassword: FunctionComponent<ChangePasswordProps> = ({
  handleHideChangePassword,
  handleSetInLineAlert,
  inLineAlertProps,
}) => {
  const { passwordConfigs } = useGetStoreConfigs();

  const {
    hideChangePassword,
    handleOnBlurPassword,
    handleConfirmPasswordChange,
    handleNewPasswordChange,
    handleCurrentPasswordChange,
    mutationChangePassword,
    currentPassword,
    newPassword,
    confirmPassword,
    passwordErrors,
    submitLoading,
    isClickSubmit,
  } = useChangePassword({
    passwordConfigs,
    handleSetInLineAlert,
    handleHideChangePassword,
  });

  const { isValidUniqueSymbols, defaultLengthMessage } =
    usePasswordValidationMessage({
      password: newPassword,
      isClickSubmit,
      passwordConfigs,
    });

  const translations = useText({
    containerTitle:
      'Account.minifiedView.CustomerInformation.changePassword.containerTitle',
    currentPasswordPlaceholder:
      'Account.minifiedView.CustomerInformation.changePassword.currentPassword.placeholder',
    currentPasswordFloatingLabel:
      'Account.minifiedView.CustomerInformation.changePassword.currentPassword.floatingLabel',
    newPasswordPlaceholder:
      'Account.minifiedView.CustomerInformation.changePassword.newPassword.placeholder',
    newPasswordFloatingLabel:
      'Account.minifiedView.CustomerInformation.changePassword.newPassword.floatingLabel',
    confirmPasswordPlaceholder:
      'Account.minifiedView.CustomerInformation.changePassword.confirmPassword.placeholder',
    confirmPasswordFloatingLabel:
      'Account.minifiedView.CustomerInformation.changePassword.confirmPassword.floatingLabel',
    buttonSecondary:
      'Account.minifiedView.CustomerInformation.changePassword.buttonSecondary',
    buttonPrimary:
      'Account.minifiedView.CustomerInformation.changePassword.buttonPrimary',
  });

  return (
    <Card className="account-change-password" variant="secondary">
      <Header
        title={translations.containerTitle}
        divider={false}
        className={'account-change-password__title'}
      />
      {inLineAlertProps.text ? (
        <InLineAlert
          className="account-change-password__notification"
          type={inLineAlertProps.type}
          variant="secondary"
          heading={inLineAlertProps.text}
          icon={inLineAlertProps.icon}
          data-testid="changePasswordInLineAlert"
        />
      ) : null}
      <div className="account-change-password__fields">
        <InputPassword
          className="account-change-password__fields-item"
          autoComplete="currentPassword"
          name="currentPassword"
          placeholder={translations.currentPasswordPlaceholder}
          floatingLabel={translations.currentPasswordFloatingLabel}
          errorMessage={passwordErrors.currentPassword}
          defaultValue={currentPassword}
          onValue={handleCurrentPasswordChange}
          onBlur={handleOnBlurPassword}
        />
        <InputPassword
          className="account-change-password__fields-item"
          autoComplete="newPassword"
          name="newPassword"
          placeholder={translations.newPasswordPlaceholder}
          floatingLabel={translations.newPasswordFloatingLabel}
          minLength={passwordConfigs?.minLength}
          validateLengthConfig={defaultLengthMessage}
          uniqueSymbolsStatus={isValidUniqueSymbols}
          requiredCharacterClasses={passwordConfigs?.requiredCharacterClasses}
          errorMessage={getErrorMessage(
            isValidUniqueSymbols,
            defaultLengthMessage,
            isClickSubmit,
            newPassword,
            passwordErrors
          )}
          defaultValue={newPassword}
          onValue={handleNewPasswordChange}
          onBlur={handleOnBlurPassword}
        />
        <InputPassword
          className="account-change-password__fields-item"
          autoComplete="confirmPassword"
          name="confirmPassword"
          placeholder={translations.confirmPasswordPlaceholder}
          floatingLabel={translations.confirmPasswordFloatingLabel}
          errorMessage={passwordErrors.confirmPassword}
          defaultValue={confirmPassword}
          onValue={handleConfirmPasswordChange}
          onBlur={handleOnBlurPassword}
        />
      </div>

      <div className="account-change-password__actions">
        <Button
          // @ts-ignore
          type="button"
          disabled={submitLoading}
          onClick={hideChangePassword}
          variant="secondary"
        >
          {translations.buttonSecondary}
        </Button>
        <Button
          variant="primary"
          // @ts-ignore
          type="button"
          disabled={submitLoading}
          onClick={mutationChangePassword}
        >
          {translations.buttonPrimary}
        </Button>
      </div>
    </Card>
  );
};
